import styled from '@emotion/styled';
import { debounce } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import Announcement, {
  AnnouncementCardSkeleton,
} from '../../../components/Announcement';
import { EmptyStateCardBody } from '../../../components/EmptyStateCard';
import GlobalTableFilter from '../../../components/GlobalTableFilter';
import PageHeader from '../../../components/PageHeader';
import { useHubAnnouncementsQuery } from '../../../__generated__/graphql';
import {
  mapHubAnnouncements,
  searchItems,
  useEmojiChange,
  useMarkAsRead,
} from './logic';
import { useFlairBreadcrumbHook } from '../../../../../hooks/useFlairBreadcrumbHook';

const i18Path = 'people.announcements';

const Content: React.FC<React.PropsWithChildren<unknown>> = () => {
  const t = useNamespacedTranslation(i18Path);

  const { data, loading } = useHubAnnouncementsQuery();

  const [searchText, setSearchText] = useState<string>('');

  const allAnnouncements = useMemo(
    () => mapHubAnnouncements(data?.hubAnnouncements),
    [data?.hubAnnouncements],
  );

  const announcements = useMemo(
    () => searchItems(allAnnouncements, searchText),
    [allAnnouncements, searchText],
  );

  const { onEmojiChange, updatingItems } = useEmojiChange(announcements);

  useMarkAsRead(allAnnouncements);

  const onSearch = useCallback(
    (filter: string) => {
      debounce(setSearchText, 300)(filter);
    },
    [setSearchText],
  );

  if (loading) {
    return (
      <div>
        <AnnouncementCardSkeleton />
        <AnnouncementCardSkeleton />
      </div>
    );
  }

  return (
    <Card>
      <Card.Header>
        <Row>
          <Col>
            <GlobalTableFilter
              placeholder={t('search')}
              onFilterChange={onSearch}
            />
          </Col>
        </Row>
      </Card.Header>
      <CardsBody>
        {announcements.length > 0 ? (
          announcements.map((a, i) => (
            <Announcement
              key={i}
              id={a.id}
              title={a.title}
              body={a.body}
              createdDate={a.createdDate}
              employee={a.employee}
              emojis={a.emojis}
              onEmojiChange={onEmojiChange}
              loadingEmojis={updatingItems.includes(a.id)}
              commentsCount={a.commentsCount}
              isTracked={a.isTracked}
              isRead={a.isRead}
              isPinned={a.isPinned}
              attachments={a.attachments}
            />
          ))
        ) : (
          <EmptyStateCardBody
            title={t('noAnnouncementsToShow')}
            subtitle={t('thereAreNoAnnouncements')}
          />
        )}
      </CardsBody>
    </Card>
  );
};

const AnnouncementContainerI18Path = 'navigation.menuItems.company';

const AnnouncementContainer: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const t = useNamespacedTranslation(AnnouncementContainerI18Path);
  const title = t('subMenuItems.announcement.title');

  useFlairBreadcrumbHook([
    { label: t('title') },
    {
      label: title,
    },
  ]);

  return (
    <>
      <PageHeader title={title} />
      <Content />
    </>
  );
};

const CardsBody = styled(Card.Body)`
  > .card:last-child {
    margin-bottom: 0;
  }
`;

export default AnnouncementContainer;
